if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        // File line item properties (ex.: upload de arte) NÃO são suportados
        // pela API AJAX do carrinho (/cart/add.js). Quando há um arquivo
        // selecionado, NÃO chamamos preventDefault: deixamos o submit nativo
        // (multipart → /cart/add) acontecer. Sem arquivo, o fluxo AJAX
        // normal (drawer) segue inalterado.
        const fileInput = this.form.querySelector('input[type="file"]');
        if (fileInput && fileInput.files && fileInput.files.length > 0) {
          return;
        }

        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        // Resolve cart no submit, validando que o Web Component foi promovido
        // (typeof renderContents === 'function'). Mesmo pattern de quick-view.js
        // que funciona. Sem typeof, pegamos elemento HTML inerte (constructor não
        // rodou ainda) e os métodos quebram, caindo no fallback /cart.
        const cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
          this.cart = cartDrawerEl;
        } else {
          const cartNotificationEl = document.querySelector('cart-notification');
          if (cartNotificationEl && typeof cartNotificationEl.renderContents === 'function') {
            this.cart = cartNotificationEl;
          } else {
            this.cart = null;
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const returnTo = formData.get('return_to');
        // Yampi: redireciona para o return_to em qualquer valor (ex.: /cart), não só /checkout.
        const redirectToCheckout = !!returnTo;
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
          // UI otimista: abre o drawer na hora com estado pendente em vez de
          // travar a tela no await do /cart/add.js (que inclui o recálculo
          // server-side de descontos e pode levar vários segundos com cupom).
          // A resposta reconcilia via renderContents; em erro, desfazemos.
          if (!redirectToCheckout && !this.closest('quick-add-modal') && typeof this.cart.openPending === 'function') {
            this.cart.openPending();
          }
          // Bubble otimista: reflete a quantidade adicionada imediatamente;
          // a resposta reconcilia via sections (cart-icon-bubble).
          if (!redirectToCheckout) {
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            const countBubble =
              cartIconBubble && cartIconBubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
            if (cartIconBubble && countBubble) {
              this.previousCartIconBubbleHTML = cartIconBubble.innerHTML;
              const addedQty = parseInt(formData.get('quantity'), 10) || 1;
              countBubble.textContent = (parseInt(countBubble.textContent, 10) || 0) + addedQty;
            }
          }
        }
        formData.delete('return_to');
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            if (response.ok) return response.json();
            try {
              return await response.json();
            } catch (e) {
              throw new Error(`Cart add failed: HTTP ${response.status} (non-JSON response)`);
            }
          })
          .then((response) => {
            if (response.status) {
              this.restoreOptimisticCart();
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (redirectToCheckout) {
              window.location = returnTo;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            this.restoreOptimisticCart();
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      restoreOptimisticCart() {
        if (this.cart && typeof this.cart.closePending === 'function') {
          this.cart.closePending();
        }
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && typeof this.previousCartIconBubbleHTML === 'string') {
          cartIconBubble.innerHTML = this.previousCartIconBubbleHTML;
        }
        this.previousCartIconBubbleHTML = undefined;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
